import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Camera, Flame, RefreshCw, Salad, SlidersHorizontal, Triangle, type LucideIcon } from "lucide-react";
import { toast } from "sonner";
import { useIsFetching } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useBranch } from "@/context/branch-context";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { Ago, ErrorBlock, FetchingDot, LoadingBlock } from "@/monitoring/components/primitives";
import { formatCount } from "@/monitoring/format";
import { SERIES, STATUS } from "@/monitoring/palette";
import {
  classifySaveError,
  errorMessage,
  useEvaluationDistributions,
  useEvaluationSettings,
  useEvaluationSettingsHistory,
  useSaveEvaluationSettings,
} from "./api";
import { FlagCard, ThresholdCard, type FlagControl } from "./components/setting-cards";
import { HistorySection } from "./components/history-section";
import { SaveDialog } from "./components/save-dialog";
import type { DistributionState } from "./components/distribution-panel";
import {
  firesAt,
  pendingChanges,
  pruneDraft,
  sameValue,
  updateValues,
  type Draft,
  type PendingChange,
} from "./model";
import { DISTRIBUTION_DAYS, type DistributionDays, type EvaluationSetting, type SettingValue } from "./types";
import { useSettingText } from "./use-setting-text";

const GROUP_ICONS: Record<string, LucideIcon> = {
  photo_quality: Camera,
  doneness: Flame,
  pizza_saver: Triangle,
  extra_ingredients: Salad,
};

/**
 * Colour of the firing side of a group's histograms, the same as its verdict elsewhere in the admin:
 * Invalid photo amber, NOK orange. "On a plate" is no problem (it waives the saver), so it gets a plain
 * series hue instead of a status colour.
 */
const GROUP_FAIL_COLORS: Record<string, string> = {
  photo_quality: STATUS.warning,
  doneness: STATUS.serious,
  pizza_saver: SERIES[0],
  extra_ingredients: SERIES[1],
};

/** the app's toasts sit bottom right, over the save bar's Save button that the 409 message asks for */
const TOAST_POSITION = "top-right" as const;

/** unsaved edits survive moving to another page of the admin (this tab only) */
const DRAFT_STORAGE_KEY = "pizza-admin:evaluation-settings:draft:v1";

interface EditState {
  draft: Draft;
  /** the settings version the edits started from (optimistic lock of the save) */
  baseVersion: number | null;
}

function readStoredEdits(): EditState {
  try {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return { draft: {}, baseVersion: null };
    const parsed = JSON.parse(raw) as Partial<EditState>;
    const draft: Draft = {};
    for (const [key, value] of Object.entries(parsed.draft ?? {})) {
      if (typeof value === "number" || typeof value === "boolean") draft[key] = value;
    }
    return { draft, baseVersion: typeof parsed.baseVersion === "number" ? parsed.baseVersion : null };
  } catch {
    return { draft: {}, baseVersion: null };
  }
}

const EMPTY_SETTINGS: EvaluationSetting[] = [];

/** ISO stamp without a zone is UTC (the backend's naive datetimes) */
const parseStamp = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const ts = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`);
  return Number.isFinite(ts) ? ts : null;
};

/**
 * The developer page "Evaluation": the production thresholds and switches of the evaluation grouped as
 * photo quality / doneness / pizza saver, each threshold with the histogram of its metric. Edits stay
 * local until saved; the save shows old -> new and is guarded by the settings version.
 */
export function EvaluationSettingsBoard() {
  const { t, tp, formatNumber, intlLocale } = useLocalization();
  const settingsQuery = useEvaluationSettings();
  const historyQuery = useEvaluationSettingsHistory(50);
  const [days, setDays] = useState<DistributionDays>(7);
  const [branchId, setBranchId] = useState<string | null>(null);
  const distQuery = useEvaluationDistributions(days, branchId);
  const save = useSaveEvaluationSettings();
  const fetchingAny = useIsFetching({ queryKey: ["evaluation-settings"] }) > 0;

  const data = settingsQuery.data;
  const settings = data?.settings ?? EMPTY_SETTINGS;
  const text = useSettingText(data?.settings);
  const byKey = useMemo(() => new Map(settings.map((s) => [s.key, s])), [settings]);

  const [{ draft, baseVersion }, setEdits] = useState<EditState>(readStoredEdits);
  const changes = useMemo(() => pendingChanges(draft, settings), [draft, settings]);
  const dirty = changes.length > 0;

  // a reload (refresh, save elsewhere, 409) drops the edits that now equal the saved values
  useEffect(() => {
    if (!data) return;
    setEdits((prev) => {
      const next = pruneDraft(prev.draft, data.settings);
      const empty = Object.keys(next).length === 0;
      const version = empty ? null : prev.baseVersion ?? data.version;
      return next === prev.draft && version === prev.baseVersion ? prev : { draft: next, baseVersion: version };
    });
  }, [data]);

  useEffect(() => {
    try {
      if (Object.keys(draft).length === 0) sessionStorage.removeItem(DRAFT_STORAGE_KEY);
      else sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ draft, baseVersion }));
    } catch {
      // private mode: the edits live as long as the page
    }
  }, [draft, baseVersion]);

  // leaving the page (reload, close) with unsaved production thresholds asks first
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const setValue = useCallback(
    (key: string, value: SettingValue) => {
      const setting = byKey.get(key);
      if (!setting || !data) return;
      setEdits((prev) => {
        const next = { ...prev.draft };
        if (sameValue(value, setting.value)) delete next[key];
        else next[key] = value;
        const empty = Object.keys(next).length === 0;
        return { draft: next, baseVersion: empty ? null : prev.baseVersion ?? data.version };
      });
    },
    [byKey, data],
  );

  const edited = (setting: EvaluationSetting): SettingValue =>
    setting.key in draft ? draft[setting.key] : setting.value;
  const isDirty = (setting: EvaluationSetting) =>
    setting.key in draft && !sameValue(draft[setting.key], setting.value);
  const flagControl = (setting: EvaluationSetting): FlagControl => ({
    setting,
    value: edited(setting) as boolean,
    dirty: isDirty(setting),
    onChange: (value) => setValue(setting.key, value),
  });

  const windowLabel = t(`evalSettings.data.long.${days}`);
  const distState = (metric: string | null): DistributionState => ({
    dist: metric ? distQuery.data?.metrics?.[metric] : undefined,
    loading: distQuery.isLoading,
    fetching: distQuery.isFetching,
    stale: distQuery.isPlaceholderData,
    error: distQuery.error ? errorMessage(distQuery.error) : null,
    onRetry: () => void distQuery.refetch(),
    windowLabel,
  });

  // groups in the order the backend lists the settings; enforce flags render inside their threshold
  const groups = useMemo(() => {
    const order: string[] = [];
    const members = new Map<string, EvaluationSetting[]>();
    for (const setting of settings) {
      if (!members.has(setting.group)) {
        order.push(setting.group);
        members.set(setting.group, []);
      }
      members.get(setting.group)!.push(setting);
    }
    return order.map((group) => ({ group, settings: members.get(group)! }));
  }, [settings]);
  const embeddedFlags = useMemo(
    () =>
      new Set(
        settings
          .filter((s) => s.kind === "threshold" && s.enforce_key && byKey.has(s.enforce_key))
          .map((s) => s.enforce_key as string),
      ),
    [settings, byKey],
  );

  // --- save -----------------------------------------------------------------------------------------
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const discard = () => setEdits({ draft: {}, baseVersion: null });

  const confirmSave = async () => {
    if (!data || !changes.length) return;
    setSaveError(null);
    try {
      const result = await save.mutateAsync({ version: baseVersion ?? data.version, values: updateValues(changes) });
      setEdits({ draft: {}, baseVersion: null });
      setConfirmOpen(false);
      toast.success(t("evalSettings.toast.saved", { version: result.version }), { position: TOAST_POSITION });
    } catch (error) {
      const failure = classifySaveError(error);
      if (failure.kind === "conflict") {
        setConfirmOpen(false);
        const fresh = await settingsQuery.refetch();
        void historyQuery.refetch();
        const latest = fresh.data;
        if (latest) {
          setEdits((prev) => {
            const next = pruneDraft(prev.draft, latest.settings);
            return { draft: next, baseVersion: Object.keys(next).length ? latest.version : null };
          });
        }
        toast.warning(t("evalSettings.toast.conflictTitle"), {
          description: t("evalSettings.toast.conflict", { version: latest?.version ?? failure.currentVersion ?? "?" }),
          duration: 15_000,
          position: TOAST_POSITION,
        });
      } else if (failure.kind === "invalid") {
        setSaveError(
          t("evalSettings.confirm.invalid", { key: failure.key ? text.label(failure.key) : "?", error: failure.message }),
        );
      } else {
        setSaveError(t("evalSettings.confirm.failed", { error: failure.message }));
      }
    }
  };

  const impact = (change: PendingChange): string | null => {
    const { setting } = change;
    if (setting.kind !== "threshold" || !setting.metric) return null;
    if (typeof change.old !== "number" || typeof change.next !== "number") return null;
    const dist = distQuery.data?.metrics?.[setting.metric];
    if (!dist || dist.n <= 0) return null;
    const from = firesAt(dist, change.old);
    const to = firesAt(dist, change.next);
    const pp = ((to - from) / dist.n) * 100;
    return t("evalSettings.confirm.impact", {
      window: windowLabel,
      from: formatCount(from),
      to: tp("count.pizzas", to, { count: formatCount(to) }),
      delta: t("evalSettings.stats.pp", { value: formatNumber(pp, { signDisplay: "exceptZero", maximumFractionDigits: 1 }) }),
    });
  };

  const refreshAll = () => {
    void settingsQuery.refetch();
    void historyQuery.refetch();
    void distQuery.refetch();
  };

  const updatedAt = parseStamp(data?.updated_at);
  const overriddenCount = settings.filter((s) => s.overridden).length;
  const since = parseStamp(distQuery.data?.since);

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="flex flex-col gap-2 px-4 pt-4 lg:px-6 lg:pt-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex min-w-0 items-center gap-2">
            <SlidersHorizontal className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
            <h1 className="text-2xl font-semibold tracking-tight">{t("evalSettings.title")}</h1>
          </div>
          {data && <Pill>{t("evalSettings.version", { version: data.version })}</Pill>}
          {overriddenCount > 0 && <Pill>{t("evalSettings.overriddenCount", { count: overriddenCount })}</Pill>}
          <div className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              className="h-9 w-9 p-0"
              onClick={refreshAll}
              title={t("evalSettings.reload")}
              aria-label={t("evalSettings.reload")}
            >
              <RefreshCw className={cn("h-4 w-4", fetchingAny && "animate-spin")} />
            </Button>
          </div>
        </div>
        <p className="max-w-3xl text-sm text-muted-foreground">{t("evalSettings.description")}</p>
        {data && (
          <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
            {data.version === 0 ? (
              t("evalSettings.neverChanged")
            ) : (
              <>
                <span className="min-w-0 break-all">
                  {t("evalSettings.lastChange", { who: data.updated_by?.email ?? t("evalSettings.unknownUser") })}
                </span>
                <span aria-hidden>·</span>
                <Ago ts={updatedAt} />
              </>
            )}
          </p>
        )}
      </div>

      {/* sticky from tablets up; on phones it would take a sixth of the screen */}
      <div className="z-20 mt-3 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:sticky md:top-12">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 lg:px-6">
          <span className="hidden text-xs font-medium text-muted-foreground sm:inline">{t("evalSettings.data.label")}</span>
          <ToggleGroup
            type="single"
            size="sm"
            variant="outline"
            value={String(days)}
            onValueChange={(value) => value && setDays(Number(value) as DistributionDays)}
            aria-label={t("evalSettings.data.window")}
            className="gap-0.5"
          >
            {DISTRIBUTION_DAYS.map((d) => (
              <ToggleGroupItem
                key={d}
                value={String(d)}
                className="h-8 whitespace-nowrap px-2.5 text-xs"
                aria-label={t(`evalSettings.data.long.${d}`)}
              >
                {t(`evalSettings.data.days.${d}`)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <BranchSelect value={branchId} onChange={setBranchId} />
          <span className="flex min-w-0 items-center gap-1.5 text-xs tabular-nums text-muted-foreground">
            {distQuery.data && (
              <>
                {tp("count.pizzas", distQuery.data.pizzas, { count: formatCount(distQuery.data.pizzas) })}
                {since && (
                  <span className="hidden sm:inline">
                    {" "}
                    {t("evalSettings.data.since", {
                      date: new Date(since).toLocaleString(intlLocale, {
                        day: "numeric",
                        month: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }),
                    })}
                  </span>
                )}
              </>
            )}
            <FetchingDot active={distQuery.isFetching} />
          </span>
          {groups.length > 1 && (
            <nav className="ml-auto hidden items-center gap-0.5 xl:flex" aria-label={t("evalSettings.title")}>
              {[...groups.map((g) => ({ id: `group-${g.group}`, label: text.groupLabel(g.group) })),
                { id: "history", label: t("evalSettings.history.title") }].map((link) => (
                <Button
                  key={link.id}
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs text-muted-foreground"
                  onClick={() => document.getElementById(link.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                >
                  {link.label}
                </Button>
              ))}
            </nav>
          )}
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-8 px-4 py-5 lg:px-6">
        {settingsQuery.error && !data && (
          <ErrorBlock
            message={t("evalSettings.loadFailed", { reason: errorMessage(settingsQuery.error) })}
            onRetry={() => void settingsQuery.refetch()}
          />
        )}
        {!data && settingsQuery.isLoading && (
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
              <LoadingBlock key={i} height={260} className="rounded-xl" />
            ))}
          </div>
        )}

        {groups.map(({ group, settings: members }) => {
          const Icon = GROUP_ICONS[group] ?? SlidersHorizontal;
          const description = text.groupDescription(group);
          const failColor = GROUP_FAIL_COLORS[group] ?? STATUS.serious;
          const outcome = text.groupFires(group);
          return (
            <section key={group} id={`group-${group}`} className="flex min-w-0 scroll-mt-28 flex-col gap-3">
              <div className="flex flex-col gap-0.5">
                <h2 className="flex items-center gap-2 text-base font-semibold">
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  {text.groupLabel(group)}
                </h2>
                {description && <p className="text-xs text-muted-foreground">{description}</p>}
              </div>
              {members.map((setting) => {
                if (setting.kind === "threshold" && typeof setting.value === "number") {
                  const enforceSetting = setting.enforce_key ? byKey.get(setting.enforce_key) : undefined;
                  const requiresSetting = setting.requires ? byKey.get(setting.requires) : undefined;
                  return (
                    <ThresholdCard
                      key={setting.key}
                      setting={setting}
                      value={edited(setting) as number}
                      dirty={isDirty(setting)}
                      onChange={(value) => setValue(setting.key, value)}
                      enforce={enforceSetting ? flagControl(enforceSetting) : null}
                      requires={
                        requiresSetting
                          ? { setting: requiresSetting, value: edited(requiresSetting) === true }
                          : null
                      }
                      distribution={setting.metric ? distState(setting.metric) : null}
                      failColor={failColor}
                      outcome={outcome}
                      text={text}
                    />
                  );
                }
                if (typeof setting.value === "boolean" && !embeddedFlags.has(setting.key)) {
                  return <FlagCard key={setting.key} control={flagControl(setting)} text={text} />;
                }
                return null;
              })}
            </section>
          );
        })}

        {data && (
          <HistorySection
            items={historyQuery.data?.items}
            loading={historyQuery.isLoading}
            fetching={historyQuery.isFetching}
            error={historyQuery.error ? errorMessage(historyQuery.error) : null}
            onRetry={() => void historyQuery.refetch()}
            text={text}
          />
        )}
      </div>

      {dirty && (
        <div className="sticky bottom-0 z-30 mt-auto border-t bg-background/95 shadow-[0_-12px_24px_-16px_rgba(0,0,0,0.8)] backdrop-blur supports-[backdrop-filter]:bg-background/85">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:px-6">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />
              <div className="min-w-0">
                <div className="text-sm font-medium">{tp("evalSettings.save.pending", changes.length)}</div>
                <div className="hidden text-xs text-muted-foreground sm:block">{t("evalSettings.save.hint")}</div>
              </div>
            </div>
            <div className="flex w-full gap-2 sm:w-auto">
              <Button variant="outline" className="h-10 flex-1 sm:h-9 sm:flex-none" onClick={discard}>
                {t("evalSettings.save.discard")}
              </Button>
              <Button
                className="h-10 flex-1 sm:h-9 sm:flex-none"
                onClick={() => {
                  setSaveError(null);
                  setConfirmOpen(true);
                }}
              >
                {t("evalSettings.save.review")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <SaveDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        changes={changes}
        impact={impact}
        saving={save.isPending}
        error={saveError}
        onConfirm={() => void confirmSave()}
        text={text}
      />
    </div>
  );
}

function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-md border px-2 text-xs font-medium text-muted-foreground">
      {children}
    </span>
  );
}

/** histograms of one branch (pizza.branch_id is the BiteBerry uuid of the branch) */
function BranchSelect({ value, onChange }: { value: string | null; onChange: (value: string | null) => void }) {
  const { t } = useLocalization();
  const { branches } = useBranch();
  const options = useMemo(() => {
    const seen = new Set<string>();
    return (branches ?? [])
      .filter((branch) => branch.bb_branch_id && !seen.has(branch.bb_branch_id) && seen.add(branch.bb_branch_id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [branches]);

  return (
    <Select value={value ?? "all"} onValueChange={(next) => onChange(next === "all" ? null : next)}>
      <SelectTrigger className="h-8 w-[10.5rem] text-xs sm:w-[14rem]" aria-label={t("evalSettings.data.branch")}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all" className="text-xs">
          {t("evalSettings.data.allBranches")}
        </SelectItem>
        {options.length > 0 && <SelectSeparator />}
        {options.map((branch) => (
          <SelectItem key={branch.bb_branch_id} value={branch.bb_branch_id} className="text-xs">
            {branch.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
